import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Button, Card, Form, Input, Select, message, Spin } from "antd"
import { ArrowLeftOutlined } from "@ant-design/icons"
import { articleService, type ArticleFormParams } from "@/services/article/articleApi"
import TinyMceEditor from "@/components/TinyMceEditor"

export const ArticleFormPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const [form] = Form.useForm()
  const [content, setContent] = useState("")

  // Fetch article details if editing
  const { data: detailResult, isFetching } = useQuery({
    queryKey: ["article_detail", id],
    queryFn: () => articleService.detail(Number(id)),
    enabled: isEdit,
  })

  useEffect(() => {
    if (isEdit && detailResult?.data) {
      const data = detailResult.data
      form.setFieldsValue({
        title: data.title,
        type: data.type,
        status: data.status,
      })
      setContent(data.content || "")
    }
  }, [isEdit, detailResult, form])

  const saveMutation = useMutation({
    mutationFn: (params: ArticleFormParams) => {
      if (isEdit) {
        return articleService.update(Number(id), params)
      }
      return articleService.create(params)
    },
    onSuccess: (res) => {
      if (res.code === 200) {
        message.success(isEdit ? "Cập nhật bài viết thành công" : "Tạo bài viết mới thành công")
        navigate("/article")
      } else {
        message.error(res.message || "Thao tác thất bại")
      }
    },
    onError: (err: any) => {
      message.error(err.message || "Có lỗi xảy ra")
    },
  })

  const onFinish = (values: any) => {
    saveMutation.mutate({
      title: values.title,
      type: values.type,
      status: values.status,
      content,
    })
  }

  if (isEdit && isFetching) {
    return (
      <div className="flex h-full items-center justify-center py-20">
        <Spin size="large" tip="Đang tải thông tin bài viết..." />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Button type="default" shape="circle" icon={<ArrowLeftOutlined />} onClick={() => navigate("/article")} />
        <h1 className="font-bold text-xl">{isEdit ? "Cập nhật bài viết" : "Tạo bài viết mới"}</h1>
      </div>

      <Card className="shadow-sm">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            type: "notification",
            status: "draft",
          }}
        >
          <Form.Item
            name="title"
            label="Tiêu đề bài viết"
            rules={[
              { required: true, message: "Vui lòng nhập tiêu đề bài viết" },
              { max: 255, message: "Tiêu đề không được vượt quá 255 ký tự" },
            ]}
          >
            <Input placeholder="Nhập tiêu đề bài viết..." disabled={saveMutation.isPending} />
          </Form.Item>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Form.Item name="type" label="Loại bài viết" rules={[{ required: true, message: "Vui lòng chọn loại" }]}>
              <Select disabled={saveMutation.isPending} options={[{ label: "Thông báo", value: "notification" }]} />
            </Form.Item>

            <Form.Item
              name="status"
              label="Trạng thái"
              rules={[{ required: true, message: "Vui lòng chọn trạng thái" }]}
            >
              <Select
                disabled={saveMutation.isPending}
                options={[
                  { label: "Nháp (Draft)", value: "draft" },
                  { label: "Xuất bản (Published)", value: "published" },
                ]}
              />
            </Form.Item>
          </div>

          <Form.Item label="Nội dung bài viết">
            <TinyMceEditor
              value={content}
              onChange={setContent}
              placeholder="Nhập nội dung thông báo, chính sách hoặc hướng dẫn tại đây..."
            />
          </Form.Item>

          <div className="mt-6 flex justify-end gap-2">
            <Button disabled={saveMutation.isPending} onClick={() => navigate("/article")}>
              Hủy
            </Button>
            <Button type="primary" htmlType="submit" loading={saveMutation.isPending}>
              Lưu lại
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  )
}

export default ArticleFormPage
